/**
 * ===== IMPORTANT NOTE =====
 * This file is loaded very early in the app initialization process, before the app is even mounted into the DOM
 * as such, composables, stores, refs, computed values, and any DOM querying/manipulation cannot be used here
 */
import { createMemoryHistory, createWebHashHistory, createRouter, type RouteRecordRaw } from 'vue-router';
import AccountsHome from '@apparatix/library/AccountsHome.vue';
import ContactsHome from '@apparatix/library/ContactsHome.vue';
import DocumentsHome from '@apparatix/library/DocumentsHome.vue';
import OpportunitiesRoot from '@apparatix/leads/OpportunitiesRoot.vue';
import FlyoutHost from '@apparatix/leads/FlyoutHost.vue';
import AccountDetailFlyout from '@apparatix/leads/AccountDetailFlyout.vue';
import ContactDetailFlyout from '@apparatix/leads/ContactDetailFlyout.vue';
import DocumentDetailFlyout from '@apparatix/leads/DocumentDetailFlyout.vue';
import LeadDetailFlyout from '@apparatix/leads/LeadDetailFlyout.vue';
import CoreFlyoutHost from '@apparatix/library/CoreFlyoutHost.vue';
import ActivitiesHome from '@apparatix/library/ActivitiesHome.vue';
import type { Component } from 'vue';
import { AppStore } from '@apparatix/stores/AppStore';

const crmDetailTabNames = [
  'overview',
  'relationships',
  'deals',
  'activity',
  'files',
  'profile',
];

const buildCrmFlyoutChildren = (baseComponent: Component, hideClose?: boolean) => [
  {
    path: 'detail',
    component: baseComponent,
    children: [
      {
        path: 'account/:id/:activeTab?',
        component: AccountDetailFlyout,
        props: route => ({ id: Number(route.params.id), activeTab: route.params.activeTab }),
      },
      {
        path: 'contact/:id/:activeTab?',
        component: ContactDetailFlyout,
        props: route => ({ id: Number(route.params.id), activeTab: route.params.activeTab, hideClose }),
      },
      {
        path: 'document/:id/:activeTab?',
        component: DocumentDetailFlyout,
        props: route => ({ id: Number(route.params.id), activeTab: route.params.activeTab }),
      },
      {
        path: 'lead/:id/:activeTab?',
        component: LeadDetailFlyout,
        props: route => ({ id: Number(route.params.id), type: 'Lead', activeTab: route.params.activeTab }),
      },
      {
        path: 'opportunity/:id/:activeTab?',
        component: LeadDetailFlyout,
        props: route => ({ id: Number(route.params.id), type: 'Opportunity', activeTab: route.params.activeTab }),
      },
    ],
  },
];

const routes: RouteRecordRaw[] = [
  {
    path: '/crm/accounts',
    component: AccountsHome,
    children: buildCrmFlyoutChildren(FlyoutHost),
    meta: {
      basePath: '/crm/accounts',
      appStore: {
        parent: 'Sales',
        child: 'Accounts',
      },
    },
  },
  {
    path: '/crm/contacts',
    component: ContactsHome,
    children: buildCrmFlyoutChildren(FlyoutHost),
    meta: {
      basePath: '/crm/contacts',
      appStore: {
        parent: 'Sales',
        child: 'Contacts',
      },
    },
  },
  {
    path: '/crm/documents',
    component: DocumentsHome,
    children: buildCrmFlyoutChildren(FlyoutHost),
    meta: {
      basePath: '/crm/documents',
      appStore: {
        parent: 'Sales',
        child: 'Documents',
      },
    },
  },
  {
    path: '/crm/leads',
    component: OpportunitiesRoot,
    props: { type: 'Lead' },
    children: buildCrmFlyoutChildren(FlyoutHost),
    meta: {
      basePath: '/crm/leads',
      appStore: {
        parent: 'Sales',
        child: 'Leads',
      },
    },
  },
  {
    path: '/crm/opportunities',
    component: OpportunitiesRoot,
    props: { type: 'Opportunity' },
    children: buildCrmFlyoutChildren(FlyoutHost),
    meta: {
      basePath: '/crm/opportunities',
      appStore: {
        parent: 'Sales',
        child: 'Opportunities',
      },
    },
  },
  {
    path: '/crm/activities',
    component: ActivitiesHome,
    meta: {
      basePath: '/crm/activities',
      appStore: {
        parent: 'Sales',
        child: 'Activities',
      },
    },
  },
  {
    path: '/core',
    meta: {
      basePath: '/core',
      flyoutWidthClass: '!w-full',
    },
    children: buildCrmFlyoutChildren(CoreFlyoutHost, true),
  },
];

const getHistoryProvider = () => import.meta.env.DEV
  ? createWebHashHistory()
  : createMemoryHistory();

const router = createRouter({
  history: getHistoryProvider(),
  routes,
});

router.onError(e => console.log('route error', e));

router.beforeEach(to => {
  if (AppStore && to?.meta?.appStore) {
    const meta = to.meta.appStore as { parent: string, child?: string };

    const parent = AppStore.navigationItems?.find(item => item.label === meta.parent);
    if (parent) {
      if (AppStore.activePage.label !== meta.parent) {
        AppStore.setActivePage(parent, true);
      }

      if (meta.child) {
        const child = parent.children?.find(c => c.label === meta.child);
        if (child && child.label !== AppStore.activeSubPage.label) {
          AppStore.setActiveSubPage(child, true);
        }
      }
    }
  }

  if (to.path.startsWith('/crm') && to.path.includes('/detail/') && to.params?.activeTab) {
    const { activeTab } = to.params;
    if (typeof activeTab !== 'string' || !crmDetailTabNames.includes(activeTab)) {
      return to.path.split('/').slice(0, -1).join('/');
    }
  }

  return true;
});

export { router };
